//Proffed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import { FaqSection } from '../../components/FaqSection'
import StarIcon from '@mui/icons-material/Star'
import { COLOR_SOLUTIONS, SOLUTIONS_HERO_START, SOLUTIONS_HERO_MID, CHECKCIRCLE_SIZE, SECTION_DARK, DARK_800, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import {
    marketAnalysisHighlights,
    marketAnalysisChallenges,
    marketAnalysisSolutionStack,
    marketAnalysisSteps,
    marketAnalysisOutcomes,
    marketAnalysisQualifiers,
    marketAnalysisFaqs,
    marketAnalysisRelated,
} from '../../common/SolutionMarketAnalysisContent'

const C = COLOR_SOLUTIONS

export default function MarketAnalysisFit() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Market Analysis & Fit"
                description="Understand the UK public sector market before you bid. Analyse buyer spending, identify who is winning, benchmark your competition and make a data-driven go/no-go decision with CFP Analytics."
                canonical="/solutions/market-analysis-and-fit"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">


                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Getting Started · Strategic Decision" size="small" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18,
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Find Out if UK Government is{' '}
                                <Box component="span" sx={{ color: C }}>Right for Your Business</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                Before committing budget or headcount, get the intelligence you need to make a confident, evidence-based decision
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                // height: '100%',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {marketAnalysisHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="Three questions you need to answer before you start"
                subtitle="Entering a sector without market intelligence is one of the most common and costly mistakes businesses make when approaching public sector"
                items={marketAnalysisChallenges}
                color={C}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="Start with evidence. A bespoke Market Research Study gives you the intelligence to decide confidently. If you proceed, the right tools and expert support are ready when you are"
                items={marketAnalysisSolutionStack}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />

            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="From question to confident decision"
                subtitle="Four stages from briefing to market entry using evidence behind decisions"
                steps={marketAnalysisSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                subtitle="After commissioning a Market Research Study and working through the advisory phase, this is the position you'll be in."
                items={marketAnalysisOutcomes}
                color={C}
            />

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={marketAnalysisQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />


            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                subtitle="From businesses at exactly the stage you're at now"
                items={marketAnalysisFaqs}
                color={DARK_800}
            />
            

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 9. Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                title="Ready to take the next step?"
                subtitle="Whether you're starting fresh or building on existing experience, here's where to go next"
                items={marketAnalysisRelated}
                color={C}
            />

            <TrustedPartnerSection />

            <CtaSection
                title="Ready to find out if government is right for your business?"
                subtitle="Book a no-obligation discovery call. We'll explain exactly what the Market Research Study covers and whether it's right for you"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
